/**
 * Ripia Tenant Service
 *
 * テナント操作・V2 API・ユーザー管理
 * @module auth/tenantService
 */

import logger from '../../utils/logger';
import { unwrapResponse } from '../../utils/apiResponseUtils';
import { TokenStorage } from './shared/tokenStorage';
import { AuthApiClient } from './shared/apiClient';
import {
  handleV2ResponseError,
  transformToMultiTenantError,
} from './shared/errorHandling';
import { API_CONFIG } from './constants';
import type {
  TenantContext,
  TenantInfo,
  UserInfo,
  ApiRequestConfig,
  TenantPermissionActions,
  MultiTenantUser,
} from './types';

/**
 * Tenant Service
 * テナント・ユーザー管理・V2 API
 */
export class TenantService {
  // ========================
  // V2 API Infrastructure
  // ========================

  /**
   * Generate unique request ID for tracing
   */
  private static generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get client version from environment
   */
  private static getClientVersion(): string {
    return import.meta.env?.VITE_APP_VERSION || '1.0.0';
  }

  /**
   * Sanitize headers for logging (redact sensitive values)
   */
  private static sanitizeHeaders(
    headers: Record<string, string>
  ): Record<string, string> {
    const sanitized = { ...headers };
    const sensitiveHeaders = ['authorization', 'x-api-key', 'x-secret'];

    for (const header of sensitiveHeaders) {
      const lowerHeader = header.toLowerCase();
      for (const key of Object.keys(sanitized)) {
        if (key.toLowerCase() === lowerHeader) {
          sanitized[key] = '[REDACTED]';
        }
      }
    }

    return sanitized;
  }

  /**
   * Setup multi-tenant headers for API requests
   * Adds JWT token, tenant context, and tracking headers
   */
  static async setupMultiTenantHeaders(
    customHeaders: Record<string, string> = {}
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    // Get token (Clerk or localStorage fallback)
    const token = await AuthApiClient.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add Tenant Context headers
    const tenantContext = TokenStorage.loadTenantContext();
    if (tenantContext) {
      headers['X-Tenant-ID'] = tenantContext.tenantId;
      if (tenantContext.tenantSlug || tenantContext.subdomain) {
        headers['X-Tenant-Subdomain'] =
          tenantContext.tenantSlug || tenantContext.subdomain || '';
      }

      // Development mode: add full context for debugging
      if (import.meta.env?.MODE === 'development') {
        headers['X-Tenant-Context'] = JSON.stringify({
          tenantId: tenantContext.tenantId,
          tenantName: tenantContext.tenantName,
          tenantRole: tenantContext.tenantRole,
          plan: tenantContext.plan,
        });
      }
    }

    // Request tracking headers
    headers['X-Request-ID'] = this.generateRequestId();
    headers['X-Client-Version'] = this.getClientVersion();
    if (typeof navigator !== 'undefined') {
      headers['X-User-Agent'] = navigator.userAgent;
    }

    return headers;
  }

  /**
   * Core V2 API call method
   * Handles headers, request, and error transformation
   */
  static async callV2API<T>(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<T> {
    try {
      const url = `${API_CONFIG.BASE_URL}${endpoint}`;
      const headers = await this.setupMultiTenantHeaders(config.headers);

      const requestConfig: RequestInit = {
        method: config.method || 'GET',
        headers,
      };

      // Add body if present
      if (config.body) {
        requestConfig.body =
          typeof config.body === 'string'
            ? config.body
            : JSON.stringify(config.body);
      }

      logger.log(`[V2 API] ${config.method || 'GET'} ${endpoint}`, {
        headers: this.sanitizeHeaders(headers),
      });

      const response = await AuthApiClient.fetchWithTimeout(
        url,
        requestConfig,
        config.timeout
      );

      if (!response.ok) {
        await handleV2ResponseError(response);
      }

      const rawData = await AuthApiClient.handleResponse<unknown>(response);
      return unwrapResponse<T>(rawData, `TenantService:${endpoint}`);
    } catch (error) {
      logger.error('[V2 API] Request failed:', error);
      throw transformToMultiTenantError(error);
    }
  }

  // ========================
  // V2 HTTP Method Shortcuts
  // ========================

  static async getV2<T>(
    endpoint: string,
    config: Omit<ApiRequestConfig, 'method'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, { ...config, method: 'GET' });
  }

  static async postV2<T>(
    endpoint: string,
    data: unknown,
    config: Omit<ApiRequestConfig, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data,
    });
  }

  static async putV2<T>(
    endpoint: string,
    data: unknown,
    config: Omit<ApiRequestConfig, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data,
    });
  }

  static async patchV2<T>(
    endpoint: string,
    data: unknown,
    config: Omit<ApiRequestConfig, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data,
    });
  }

  static async deleteV2<T>(
    endpoint: string,
    config: Omit<ApiRequestConfig, 'method'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, { ...config, method: 'DELETE' });
  }

  // ========================
  // URL Builders
  // ========================

  /**
   * Build V2 API URL with parameter substitution
   */
  static buildV2Url(
    endpoint: string,
    params: Record<string, string> = {}
  ): string {
    let url = endpoint;
    for (const [key, value] of Object.entries(params)) {
      url = url.replace(`{${key}}`, encodeURIComponent(value));
    }
    return url;
  }

  /**
   * V2 Users API URL Builders
   */
  static buildUsersUrl = {
    list: () => API_CONFIG.ENDPOINTS.V2_USERS.LIST,
    create: () => API_CONFIG.ENDPOINTS.V2_USERS.CREATE,
    getById: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.GET_BY_ID, { id }),
    update: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.UPDATE, { id }),
    delete: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.DELETE, { id }),
    getPermissions: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.GET_PERMISSIONS, {
        id,
      }),
    bulkAction: () => API_CONFIG.ENDPOINTS.V2_USERS.BULK_ACTION,
    debugContext: () => API_CONFIG.ENDPOINTS.V2_USERS.DEBUG_CONTEXT,
  };

  /**
   * V2 Auth API URL Builders
   */
  static buildAuthUrl = {
    login: () => API_CONFIG.ENDPOINTS.V2_AUTH.LOGIN,
    refresh: () => API_CONFIG.ENDPOINTS.V2_AUTH.REFRESH,
    logout: () => API_CONFIG.ENDPOINTS.V2_AUTH.LOGOUT,
    me: () => API_CONFIG.ENDPOINTS.V2_AUTH.ME,
    switchTenant: () => API_CONFIG.ENDPOINTS.V2_AUTH.SWITCH_TENANT,
    verifyToken: () => API_CONFIG.ENDPOINTS.V2_AUTH.VERIFY_TOKEN,
    revokeToken: () => API_CONFIG.ENDPOINTS.V2_AUTH.REVOKE_TOKEN,
    mfaSetup: () => API_CONFIG.ENDPOINTS.V2_AUTH.MFA_SETUP,
    mfaVerify: () => API_CONFIG.ENDPOINTS.V2_AUTH.MFA_VERIFY,
    passwordPolicy: () => API_CONFIG.ENDPOINTS.V2_AUTH.PASSWORD_POLICY,
    securityStatus: () => API_CONFIG.ENDPOINTS.V2_AUTH.SECURITY_STATUS,
  };

  /**
   * V2 Tenants API URL Builders
   */
  static buildTenantsUrl = {
    list: () => API_CONFIG.ENDPOINTS.V2_TENANTS.LIST,
    getCurrent: () => API_CONFIG.ENDPOINTS.V2_TENANTS.GET_CURRENT,
    getById: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_BY_ID, {
        id,
      }),
    update: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.UPDATE, { id }),
    getSettings: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_SETTINGS, {
        id,
      }),
    updateSettings: (id: string) =>
      TenantService.buildV2Url(
        API_CONFIG.ENDPOINTS.V2_TENANTS.UPDATE_SETTINGS,
        { id }
      ),
    getAnalytics: (id: string) =>
      TenantService.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_ANALYTICS, {
        id,
      }),
    getRateLimits: (id: string) =>
      TenantService.buildV2Url(
        API_CONFIG.ENDPOINTS.V2_TENANTS.GET_RATE_LIMITS,
        { id }
      ),
  };

  // ========================
  // Tenant Context Management
  // ========================

  /**
   * Get current tenant context from storage
   */
  static getCurrentTenant(): TenantContext | null {
    try {
      return TokenStorage.loadTenantContext();
    } catch (error) {
      logger.error('テナントコンテキスト取得エラー:', error);
      return null;
    }
  }

  /**
   * Get current tenant context (V2 alias)
   */
  static getCurrentTenantContextV2(): TenantContext | null {
    return this.getCurrentTenant();
  }

  /**
   * Get list of available tenants
   */
  static getTenantList(): TenantInfo[] {
    try {
      return TokenStorage.loadAvailableTenants();
    } catch (error) {
      logger.error('テナント一覧取得エラー:', error);
      return [];
    }
  }

  /**
   * Save available tenants to storage
   */
  static saveAvailableTenants(tenants: TenantInfo[]): void {
    TokenStorage.saveAvailableTenants(tenants);
  }

  /**
   * Save tenant context to storage
   */
  static saveTenantContext(context: TenantContext): void {
    TokenStorage.saveTenantContext(context);
  }

  /**
   * Create tenant context from tenant info
   */
  static createTenantContext(
    tenantInfo: TenantInfo,
    userRole: TenantContext['tenantRole'] = 'staff',
    permissions: TenantContext['permissions'] = {}
  ): TenantContext {
    return {
      id: tenantInfo.id,
      tenantId: tenantInfo.tenantId || tenantInfo.id,
      name: tenantInfo.name,
      tenantName: tenantInfo.name,
      subdomain: tenantInfo.subdomain,
      tenantSlug: tenantInfo.slug,
      planType: tenantInfo.planType,
      plan: tenantInfo.plan || tenantInfo.planType,
      features: tenantInfo.features || [],
      billingStatus: tenantInfo.billingStatus,
      userRole,
      tenantRole: userRole,
      permissions,
      currentStoreId: undefined,
      availableStores: [],
    };
  }

  /**
   * Clear multi-tenant tokens from storage
   */
  static clearMultiTenantTokens(): void {
    TokenStorage.clearMultiTenantTokens();
  }

  // ========================
  // Tenant API Methods
  // ========================

  /**
   * Get current tenant from API
   */
  static async getCurrentTenantV2(): Promise<TenantInfo> {
    return this.getV2<TenantInfo>(this.buildTenantsUrl.getCurrent());
  }

  /**
   * Get tenant by ID
   */
  static async getTenantById(id: string): Promise<TenantInfo> {
    return this.getV2<TenantInfo>(this.buildTenantsUrl.getById(id));
  }

  /**
   * Update tenant
   */
  static async updateTenant(
    id: string,
    tenantData: unknown
  ): Promise<TenantInfo> {
    return this.patchV2<TenantInfo>(
      this.buildTenantsUrl.update(id),
      tenantData
    );
  }

  /**
   * Get tenant settings
   */
  static async getTenantSettings(id: string): Promise<unknown> {
    return this.getV2(this.buildTenantsUrl.getSettings(id));
  }

  /**
   * Update tenant settings
   */
  static async updateTenantSettings(
    id: string,
    settings: unknown
  ): Promise<unknown> {
    return this.patchV2(this.buildTenantsUrl.updateSettings(id), settings);
  }

  /**
   * Get tenant analytics
   */
  static async getTenantAnalytics(id: string): Promise<unknown> {
    return this.getV2(this.buildTenantsUrl.getAnalytics(id));
  }

  /**
   * Get tenant rate limits
   */
  static async getTenantRateLimits(id: string): Promise<unknown> {
    return this.getV2(this.buildTenantsUrl.getRateLimits(id));
  }

  // ========================
  // User Management (V2 API)
  // ========================

  /**
   * Get users list with optional filters
   */
  static async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }): Promise<unknown> {
    const url = this.buildUsersUrl.list();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.search) queryParams.append('search', params.search);

    const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;
    return this.getV2(fullUrl);
  }

  /**
   * Create new user
   */
  static async createUser(userData: unknown): Promise<unknown> {
    return this.postV2(this.buildUsersUrl.create(), userData);
  }

  /**
   * Get user by ID
   */
  static async getUserById(id: string): Promise<unknown> {
    return this.getV2(this.buildUsersUrl.getById(id));
  }

  /**
   * Update user
   */
  static async updateUser(id: string, userData: unknown): Promise<unknown> {
    return this.patchV2(this.buildUsersUrl.update(id), userData);
  }

  /**
   * Delete user
   */
  static async deleteUser(id: string): Promise<unknown> {
    return this.deleteV2(this.buildUsersUrl.delete(id));
  }

  /**
   * Get user permissions
   */
  static async getUserPermissions(id: string): Promise<unknown> {
    return this.getV2(this.buildUsersUrl.getPermissions(id));
  }

  // ========================
  // Enhanced User Context
  // ========================

  /**
   * Get current user with V2 format
   */
  static getCurrentUserV2(): UserInfo | null {
    try {
      const user = TokenStorage.getUserData();
      if (!user) return null;

      // Convert legacy permissions to V2 format
      const convertPermissions = (
        legacyPermissions?: string[]
      ): Record<string, TenantPermissionActions> => {
        if (!Array.isArray(legacyPermissions)) return {};

        const permissionMap: Record<string, TenantPermissionActions> = {};
        legacyPermissions.forEach((permission) => {
          const [resource, action] = permission.split(':');
          if (!resource) return;

          if (!permissionMap[resource]) {
            permissionMap[resource] = {};
          }

          if (action) {
            if (action === 'read' || action === 'view')
              permissionMap[resource].read = true;
            else if (
              action === 'write' ||
              action === 'create' ||
              action === 'update'
            )
              permissionMap[resource].write = true;
            else if (action === 'delete' || action === 'remove')
              permissionMap[resource].delete = true;
            else if (action === 'admin' || action === 'manage')
              permissionMap[resource].admin = true;
            else if (action === 'export') permissionMap[resource].export = true;
            else if (action === 'import') permissionMap[resource].import = true;
          } else {
            // Grant basic permissions for backward compatibility
            permissionMap[resource] = {
              read: true,
              write: true,
              delete: false,
              admin: false,
            };
          }
        });

        return permissionMap;
      };

      return {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        tenantRole: user.tenantRole || 'staff',
        permissions: convertPermissions(user.permissions),
        isActive: true,
        lastLogin: user.lastLoginAt,
      };
    } catch (error) {
      logger.error('Failed to get current user:', error);
      return null;
    }
  }

  // ========================
  // JWT Generation (Mock/Dev)
  // ========================

  /**
   * Generate multi-tenant JWT (for development/testing)
   */
  static generateMultiTenantJWT(
    user: MultiTenantUser,
    tenantContext: TenantContext
  ): string {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(
      JSON.stringify({
        // Legacy payload (backward compatibility)
        sub: user.id,
        email: user.email,
        role: user.role,
        storeId: user.storeId,
        permissions: user.permissions || [],
        // Multi-tenant extended payload
        tenant_id: tenantContext.tenantId,
        tenant_name: tenantContext.tenantName,
        tenant_role: tenantContext.tenantRole,
        tenant_permissions: tenantContext.permissions,
        plan_type: tenantContext.plan,
        plan_features: tenantContext.features || [],
        current_store_id: tenantContext.currentStoreId,
        available_stores:
          tenantContext.availableStores?.map((store) =>
            typeof store === 'string' ? store : store.code || store.id
          ) || [],
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24 hours
      })
    );
    const signature = btoa('mock_signature_for_development');

    return `${header}.${payload}.${signature}`;
  }

  // ========================
  // Mock Data (Development)
  // ========================

  /**
   * Get mock tenant list for development
   * Returns simplified mock data for testing purposes
   */
  static getMockTenantList(): Partial<TenantInfo>[] {
    return [
      {
        id: 'tenant-1',
        tenantId: 'tenant-1',
        name: 'ドライヘッドスパ HITOYASUMI 銀座店',
        slug: 'hitoyasumi-ginza',
        subdomain: 'ginza',
        planType: 'standard',
        plan: 'standard',
        features: ['reservation', 'customer', 'staff', 'analytics'],
        billingStatus: 'active',
        status: 'active',
      },
      {
        id: 'tenant-2',
        tenantId: 'tenant-2',
        name: 'ドライヘッドスパ HITOYASUMI 渋谷店',
        slug: 'hitoyasumi-shibuya',
        subdomain: 'shibuya',
        planType: 'premium',
        plan: 'premium',
        features: [
          'reservation',
          'customer',
          'staff',
          'analytics',
          'api',
          'white_label',
        ],
        billingStatus: 'active',
        status: 'active',
      },
    ];
  }
}

export default TenantService;
